import React from 'react';

import { BETA_FEATURES } from '@/constants/betaFeatures';
import useBetaFeature from '@/hooks/useBetaFeature';
import { BetaBadge, Toggle, Tooltip } from '@/ui';

import './styles.css';
import type { Props } from './types';

/**
 * The switch a user flips to try a beta, or to go back to what it replaces.
 *
 * Renders nothing while the beta's stage offers no choice (hidden or
 * released), so a page can place it unconditionally.
 */
const BetaSwitch = ({ feature }: Props) => {
  const { stage, isToggleVisible, isEnabled, setEnabled } = useBetaFeature(feature);

  if (!isToggleVisible) return null;

  const { label } = BETA_FEATURES[feature];
  const hint =
    stage === 'opt_out'
      ? `The ${label.toLowerCase()} is now the default. Switch it off to go back.`
      : `Try the ${label.toLowerCase()}. It's saved to your account, and you can switch back anytime.`;

  return (
    <Tooltip content={hint} triggerClassName="inline-flex items-center gap-2">
      {isEnabled ? (
        <BetaBadge />
      ) : (
        <span className="text-sm text-uniform-gray-600 dark:text-uniform-gray-300">Try beta</span>
      )}
      <Toggle size="sm" checked={isEnabled} onChange={setEnabled} aria-label={`Use the ${label}`} />
    </Tooltip>
  );
};

export default React.memo(BetaSwitch);
